import { ref, watch, onMounted, onUnmounted } from 'vue'
import { useVehicleStore } from '../stores/vehicleStore'
import { useNavigationStore } from '../stores/navigationStore'
import { useSelectionStore } from '../stores/selectionStore'
import { usePartsStore } from '../stores/partsStore'

interface UrlParams {
  mode?: string
  reg?: string
  carId?: string
  // '1' when carId is an RMI id (graphics vehicles store the RMI id as carId). Needed because RMI
  // ids collide numerically with unrelated TecDoc carIds, so restore must pick the right lookup.
  rmi?: string
  qualId?: string
  mg?: string
  sg?: string
  item?: string
  manu?: string
  model?: string
  motor?: string
  oe?: string
}

const URL_PARAM_KEYS = ['mode', 'reg', 'carId', 'rmi', 'qualId', 'mg', 'sg', 'item', 'manu', 'model', 'motor', 'oe']

function readUrlParams(): UrlParams {
  const params = new URLSearchParams(window.location.search)
  const result: UrlParams = {}
  for (const key of URL_PARAM_KEYS) {
    const val = params.get(key)
    if (val) (result as any)[key] = val
  }
  return result
}

function writeUrlParams(params: UrlParams, push = false) {
  const url = new URL(window.location.href)
  for (const key of URL_PARAM_KEYS) {
    url.searchParams.delete(key)
  }
  for (const [key, val] of Object.entries(params)) {
    if (val != null && val !== '') {
      url.searchParams.set(key, String(val))
    }
  }

  if (push) {
    history.pushState(null, '', url.toString())
  } else {
    history.replaceState(null, '', url.toString())
  }
}

export function useUrlState() {
  const vehicleStore = useVehicleStore()
  const navStore = useNavigationStore()
  const selectionStore = useSelectionStore()
  const partsStore = usePartsStore()

  // Drill-down state captured from the URL on restore. Applied once the RMI
  // category tree finishes loading — see the watcher near the bottom.
  const pendingNavigation = ref<{ mg?: string; sg?: string; item?: string } | null>(null)

  function syncToUrl(push = false) {
    const params: UrlParams = { mode: vehicleStore.searchMode }

    // Always write carId and qualId if available (all modes need these for restore)
    if (vehicleStore.carId) params.carId = String(vehicleStore.carId)
    // Flag carId as an RMI id when graphics are available (that's when carId holds the RMI id),
    // so restore disambiguates it from a colliding TecDoc carId.
    if (vehicleStore.carId && vehicleStore.hasGraphics) params.rmi = '1'
    if (vehicleStore.qualId !== null && vehicleStore.qualId !== undefined) params.qualId = String(vehicleStore.qualId)

    if (vehicleStore.searchMode === 'regnr') {
      if (vehicleStore.regNr) params.reg = vehicleStore.regNr
    } else if (vehicleStore.searchMode === 'manual') {
      if (selectionStore.selectedManufacturerId) params.manu = String(selectionStore.selectedManufacturerId)
      if (selectionStore.selectedModelId) params.model = String(selectionStore.selectedModelId)
      if (selectionStore.selectedMotorId) params.motor = String(selectionStore.selectedMotorId)
    } else if (vehicleStore.searchMode === 'oem') {
      if (vehicleStore.oemNumber) params.oe = vehicleStore.oemNumber
    } else if (vehicleStore.searchMode === 'motorcode') {
      // motor param stores the original TecDoc carId for restore
      if (selectionStore.selectedMotorId) params.motor = String(selectionStore.selectedMotorId)
    }

    // Navigation state
    if (navStore.mainGroupId) params.mg = navStore.mainGroupId
    if (navStore.subGroupId) params.sg = navStore.subGroupId
    if (navStore.itemId) params.item = navStore.itemId

    writeUrlParams(params, push)
  }

  async function restoreFromUrl() {
    const params = readUrlParams()
    if (!params.mode) return

    // Remember RMI drill-down so we can replay it after the category tree loads.
    // Cleared here so a re-restore (e.g. on popstate) always starts fresh.
    pendingNavigation.value = (params.mg || params.sg || params.item)
      ? { mg: params.mg, sg: params.sg, item: params.item }
      : null

    vehicleStore.searchMode = params.mode as 'regnr' | 'manual' | 'oem' | 'motorcode'

    // carId is an RMI id when the URL says so. Such ids must be resolved via the RMI lookup,
    // otherwise the generic lookup resolves a numerically-colliding (and unrelated) TecDoc carId.
    // Fallback for older URLs without the flag: RMI drill-down params (mg/sg/item) only ever exist
    // for graphics vehicles, where carId IS the RMI id (the standard category flow never sets them).
    const carIdIsRmi = params.rmi === '1' || !!(params.mg || params.sg || params.item)
    const loadByCarId = async (id: number) =>
      carIdIsRmi ? vehicleStore.restoreByRmiId(id) : vehicleStore.selectMotor(id)

    if (params.mode === 'regnr') {
      if (params.carId) {
        // Fast restore: carId already known, skip Vegvesen API call
        vehicleStore.regNr = params.reg ?? ''
        await loadByCarId(Number(params.carId))
      } else if (params.reg) {
        // Full search: only have reg number
        await vehicleStore.searchByRegNr(params.reg)
      }
    } else if (params.mode === 'manual' && params.manu) {
      await selectionStore.loadManufacturers()
      await selectionStore.selectManufacturer(Number(params.manu))
      if (params.model) {
        await selectionStore.selectModel(Number(params.model))
        if (params.motor) {
          selectionStore.selectMotor(Number(params.motor))
          await vehicleStore.selectMotor(Number(params.motor))
        }
      }
    } else if (params.mode === 'oem' && params.oe) {
      vehicleStore.oemNumber = params.oe
      await partsStore.loadOemParts(params.oe)
    } else if (params.mode === 'motorcode') {
      if (params.carId) {
        // Fast restore from resolved carId (RMI id when rmi=1).
        await loadByCarId(Number(params.carId))
      } else if (params.motor) {
        // Restore from original motor carId (always a TecDoc carId).
        await vehicleStore.selectMotor(Number(params.motor))
      }
    }
  }

  function handlePopState() {
    restoreFromUrl()
  }

  // Set while we're programmatically replaying a drill-down from the URL,
  // so the navigation watcher below does not push intermediate history entries.
  let isReplayingNavigation = false

  // True while the URL still holds drill-down params we haven't consumed yet.
  // During this window, syncToUrl is suppressed entirely so the restore-then-
  // vehicle-watcher-fires sequence doesn't strip mg/sg/item before we can replay them.
  const isSyncSuppressed = () =>
    isReplayingNavigation || pendingNavigation.value !== null

  // Watch for meaningful navigation changes and push to history
  watch(
    () => [navStore.currentLevel, navStore.mainGroupId, navStore.subGroupId, navStore.itemId],
    () => {
      if (isSyncSuppressed()) return
      syncToUrl(true)
    },
  )

  // Watch for vehicle selection changes and replace URL
  watch(
    () => [vehicleStore.carId, vehicleStore.qualId, vehicleStore.searchMode],
    () => {
      if (isSyncSuppressed()) return
      syncToUrl(false)
    },
  )

  // Consume pendingNavigation once the RMI category tree has loaded.
  // This fires after restoreFromUrl → vehicleStore.selectMotor → App.vue watcher
  // → navStore.loadVehicleImage populates navStore.categories.
  watch(
    () => [navStore.categories.length, vehicleStore.carId, vehicleStore.qualId, vehicleStore.hasGraphics],
    async ([catLen, carId, qualId, hasGraphics]) => {
      if (!pendingNavigation.value) return
      if (!hasGraphics || !carId || qualId === null || qualId === undefined || !catLen) return

      const pending = pendingNavigation.value
      pendingNavigation.value = null
      isReplayingNavigation = true

      try {
        const qualIdStr = String(qualId)
        const carIdNum = carId as number

        if (pending.mg) {
          const mg = navStore.categories.find(c => String(c.mainGroupId) === pending.mg)
          if (!mg) return
          await navStore.navigateToMainGroup(qualIdStr, carIdNum, pending.mg, mg.mainGroupName)

          if (pending.sg) {
            const sg = mg.subGroups?.find(s => String(s.subGroupId) === pending.sg)
            if (!sg) return
            await navStore.navigateToSubGroup(qualIdStr, carIdNum, pending.mg, pending.sg, sg.subGroupName)

            if (pending.item) {
              const item = sg.items?.find(i => String(i.itemMpId) === pending.item)
              if (!item) return
              navStore.setProductsLevel(pending.item, item.itemMpText)
              await partsStore.loadRmiParts(qualIdStr, carIdNum, pending.mg, pending.sg, Number(pending.item))
            }
          }
        }
      } finally {
        isReplayingNavigation = false
        // Now that replay is done and pendingNavigation is cleared, write the
        // full URL once to reflect the state we just applied (including any
        // levels we failed to resolve — at least vehicle + the levels we did resolve).
        syncToUrl(false)
      }
    },
  )

  onMounted(() => {
    window.addEventListener('popstate', handlePopState)
    restoreFromUrl()
  })

  onUnmounted(() => {
    window.removeEventListener('popstate', handlePopState)
  })

  return { syncToUrl, restoreFromUrl }
}
